/**
 * Product analytics, off until a service is chosen (docs/PENDENCIAS.md): set VITE_ANALYTICS_PROVIDER
 * to plausible, umami or ga4, and VITE_ANALYTICS_ID to that service's site domain, website id or
 * measurement id. The page's content security policy allows the chosen service and nothing else
 * (vite.config.ts). Page views only: nothing typed in the app is ever sent.
 */

type Provider = 'plausible' | 'umami' | 'ga4'

const SCRIPTS: Record<Provider, (id: string) => { src: string; attrs: Record<string, string> }> = {
  plausible: (id) => ({ src: 'https://plausible.io/js/script.js', attrs: { 'data-domain': id } }),
  umami: (id) => ({ src: 'https://cloud.umami.is/script.js', attrs: { 'data-website-id': id } }),
  ga4: (id) => ({ src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`, attrs: {} }),
}

export function analyticsConfig(env: { VITE_ANALYTICS_PROVIDER?: string; VITE_ANALYTICS_ID?: string } = import.meta.env ?? {}) {
  const provider = env.VITE_ANALYTICS_PROVIDER as Provider | undefined
  const id = env.VITE_ANALYTICS_ID?.trim()
  return provider && provider in SCRIPTS && id ? { provider, id } : null
}

let started = false

/** Loads the chosen service's script once. Does nothing when none is configured. */
export function startAnalytics() {
  const config = analyticsConfig()
  if (!config || started) return
  started = true

  const { src, attrs } = SCRIPTS[config.provider](config.id)
  const script = document.createElement('script')
  script.async = true
  script.defer = true
  script.src = src
  for (const [key, value] of Object.entries(attrs)) script.setAttribute(key, value)
  document.head.appendChild(script)

  if (config.provider === 'ga4') {
    const w = window as unknown as { dataLayer: unknown[]; gtag: (...args: unknown[]) => void }
    w.dataLayer = w.dataLayer || []
    w.gtag = (...args: unknown[]) => void w.dataLayer.push(args)
    w.gtag('js', new Date())
    w.gtag('config', config.id, { anonymize_ip: true })
  }
}
